import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ReactNode, MutableRefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthContext } from '@/contexts/AuthContext';
import {
 usersAttachDatabaseStart,
 getItemStart,
 usersAttachDatabaseFinish,
} from '@/redux/users/usersSlice';
import { UsersDTO } from '@/redux/identity/identityTypes';
import { RootState } from '@/redux/store';
import { CustomGridApi } from '../XTable/types';
import { useTranslation } from 'react-i18next';

type DatabaseCheckProps = {
 item: UsersDTO;
 apiRef: MutableRefObject<CustomGridApi>;
 children: ReactNode;
};

const DatabaseCheck = ({ item, apiRef, children }: DatabaseCheckProps) => {
 const dispatch = useDispatch();
 const { loginUser } = useAuthContext();
 const { loading, success } = useSelector((state: RootState) => state.users);
 const { t } = useTranslation();

 const activeDatabaseId = loginUser?.currentlyActiveDb || '';
 const userDatabaseIds =
  item?.Databases?.map((database) => database.Id?.toString() || '') || [];
 const isActiveDatabaseAssignedToUser =
  !!activeDatabaseId && userDatabaseIds.includes(activeDatabaseId);

 const handleAttachDatabase = () => {
  if (!activeDatabaseId) {
   alert('Current user has no active database');
   return;
  }
  if (!item?.Id) {
   alert('Selected user has no ID');
   return;
  }

  dispatch(
   usersAttachDatabaseStart([
    {
     ConnectionKeyId: Number(activeDatabaseId),
     UserId: item.Id,
    },
   ]),
  );
 };

 useEffect(() => {
  if (!success?.usersAttachDatabase) {
   return;
  }
  apiRef.current.fetchData?.();
  if (!item?.Id) {
   return;
  }
  dispatch(
   getItemStart({
    table: 'users',
    id: item.Id,
   }),
  );
  dispatch(usersAttachDatabaseFinish());
 }, [apiRef, dispatch, item?.Id, success?.usersAttachDatabase]);

 if (!isActiveDatabaseAssignedToUser) {
  return (
   <Box
    sx={{
     px: 2,
     display: 'flex',
     flexDirection: 'column',
     gap: 2,
    }}
   >
    <Typography variant="body1">
     {t('user.sidebar.database.notAssigned')}
    </Typography>
    <Button
     variant="contained"
     color="primary"
     onClick={handleAttachDatabase}
     disabled={!activeDatabaseId || !!loading?.usersAttachDatabase}
    >
     {loading?.usersAttachDatabase ? (
      <CircularProgress size={28} />
     ) : (
      t('user.sidebar.database.assign')
     )}
    </Button>
   </Box>
  );
 }

 return <>{children}</>;
};

export default DatabaseCheck;
